import { Lightbulb, Scale, HeartHandshake } from "lucide-react";
import Reveal from "./Reveal";

export default function About() {
  return (
    <section id="about" className="py-20 sm:py-24 lg:py-32 bg-[#EFEAE0]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-10 lg:gap-12">
        <Reveal className="lg:col-span-5">
          <p className="text-sm sm:text-base font-bold uppercase tracking-[0.18em] text-[#FF008F] mb-4">What is Surplus Foreclosure?</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] leading-tight">
            When a home sells for more than the debt, the difference belongs to you.
          </h2>

          <div className="mt-8 relative rounded-3xl overflow-hidden border border-[#E2DDD3] shadow-xl shadow-[#0F172A]/10 aspect-[4/5] max-h-[560px]">
            <img
              src="https://customer-assets.emergentagent.com/job_foreclosure-leads/artifacts/nm4tfcu5_home-4-BlUpqm7I.jpg"
              alt="A warmly-lit Victorian family home at dusk — the kind of asset families lose to foreclosure"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#0F172A]/70 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 bg-[#F7F5F0]/95 backdrop-blur-sm rounded-2xl px-5 py-3.5 border border-[#E2DDD3] shadow-md">
              <p className="text-[10px] uppercase tracking-wider text-[#64748B]">Did you know?</p>
              <p className="font-serif-display text-base sm:text-lg text-[#0F172A] mt-0.5 leading-snug">
                Most homeowners never learn that surplus funds exist.
              </p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-7 space-y-6 text-[#334155] text-base sm:text-lg leading-relaxed">
          <p>
            Surplus foreclosure funds (also called "excess proceeds" or "overage funds") are created when a foreclosed property sells at auction for more than the amount owed on the mortgage.
          </p>
          <p>
            By law, that surplus money — minus any liens or fees — must be returned to the previous homeowner. The catch? Counties rarely notify you, claim deadlines are strict, and the paperwork is intentionally complex.
          </p>
          <p className="text-[#0F172A] italic font-serif-display text-xl border-l-4 border-[#FF008F] pl-5">
            Most homeowners never even know this money exists. That's where we come in.
          </p>

          <div className="grid sm:grid-cols-3 gap-4 pt-6">
            <Tile icon={<Lightbulb className="w-5 h-5" />} title="We find it" body="We monitor county records nationwide to identify unclaimed surplus funds tied to your property." />
            <Tile icon={<Scale className="w-5 h-5" />} title="We file it" body="Our team handles every petition, claim form, and court filing required to recover your money." />
            <Tile icon={<HeartHandshake className="w-5 h-5" />} title="You collect it" body="Once approved by the court, the funds are released to you. We only get paid when you do." />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Tile({ icon, title, body }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E2DDD3] p-5 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
      <div className="w-10 h-10 rounded-lg bg-[#FFE5F2] grid place-items-center text-[#1E3A8A] mb-3">{icon}</div>
      <h3 className="font-serif-display text-lg text-[#0F172A]">{title}</h3>
      <p className="text-sm text-[#475569] mt-1">{body}</p>
    </div>
  );
}
